import {
  calculateEngineOnStoppedTimeMs,
  calculateMovingPathDistanceStats,
  calculatePeriodMotionStats,
  calculatePathDistanceStats,
  formatPeriodClockDuration,
} from "../../helpers/operationPeriodStats";

function parseHourMeter(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseOdometer(value) {
  return parseHourMeter(value);
}

function parsePanelHectares(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Horas e hectares informados pelo operador ao assumir/finalizar (painel da máquina).
 */
export function computeOperatorPanelTotals(fieldEvents, machineMeterType) {
  const events = [...(fieldEvents || [])].sort(
    (left, right) =>
      new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()
  );

  const sessionsByUuid = new Map();
  let openSession = null;
  let totalHours = 0;
  let totalKm = 0;
  let totalHectares = 0;
  let hoursSessionCount = 0;
  let kmSessionCount = 0;
  let hectaresSessionCount = 0;
  let lastHourMeter = null;
  let lastHourMeterAt = null;
  let lastOdometer = null;
  let lastOdometerAt = null;

  const closeSession = (session) => {
    if (!session || !session.assume) return;

    const startHour = parseHourMeter(session.assume.hourMeter);
    const endHour = parseHourMeter(session.finish && session.finish.hourMeter);
    if (startHour != null && endHour != null) {
      const delta = endHour - startHour;
      if (delta > 0) {
        totalHours += delta;
        hoursSessionCount += 1;
      }
    }

    const startKm = parseOdometer(session.assume.odometer);
    let endKm = parseOdometer(session.finish && session.finish.odometer);
    if (machineMeterType === "odometer") {
      const legacyStart =
        startKm == null ? parseHourMeter(session.assume.hourMeter) : null;
      const legacyEnd =
        endKm == null && session.finish
          ? parseHourMeter(session.finish.hourMeter)
          : null;
      const resolvedStart = startKm != null ? startKm : legacyStart;
      const resolvedEnd = endKm != null ? endKm : legacyEnd;
      if (resolvedStart != null && resolvedEnd != null) {
        const delta = resolvedEnd - resolvedStart;
        if (delta > 0) {
          totalKm += delta;
          kmSessionCount += 1;
        }
      }
    } else if (startKm != null && endKm != null) {
      const delta = endKm - startKm;
      if (delta > 0) {
        totalKm += delta;
        kmSessionCount += 1;
      }
    }

    const startHa = parsePanelHectares(session.assume.panelHectares);
    const endHa = parsePanelHectares(session.finish && session.finish.panelHectares);
    if (startHa != null && endHa != null) {
      const delta = endHa - startHa;
      if (delta > 0) {
        totalHectares += delta;
        hectaresSessionCount += 1;
      }
    }
  };

  events.forEach((event) => {
    const hourMeter = parseHourMeter(event.hourMeter);
    const odometer = parseOdometer(event.odometer);
    if (
      hourMeter != null &&
      (event.type === "assume_machine" || event.type === "finish_work")
    ) {
      lastHourMeter = hourMeter;
      lastHourMeterAt = event.timestamp;
    }
    if (
      odometer != null &&
      (event.type === "assume_machine" || event.type === "finish_work")
    ) {
      lastOdometer = odometer;
      lastOdometerAt = event.timestamp;
    }

    if (event.type !== "assume_machine" && event.type !== "finish_work") {
      return;
    }

    if (event.type === "assume_machine") {
      if (openSession && !openSession.finish) {
        closeSession(openSession);
      }
      openSession = { assume: event, finish: null };
      if (event.uuid) {
        sessionsByUuid.set(event.uuid, openSession);
      }
      return;
    }

    let session = null;
    if (event.uuid && sessionsByUuid.has(event.uuid)) {
      session = sessionsByUuid.get(event.uuid);
    } else if (
      openSession &&
      openSession.assume &&
      !openSession.finish &&
      (!event.operatorId ||
        !openSession.assume.operatorId ||
        event.operatorId === openSession.assume.operatorId)
    ) {
      session = openSession;
    }

    if (session) {
      session.finish = event;
      closeSession(session);
      if (session === openSession) {
        openSession = null;
      }
    }
  });

  return {
    totalHours: hoursSessionCount > 0 ? totalHours : null,
    totalKm: kmSessionCount > 0 ? totalKm : null,
    totalHectares: hectaresSessionCount > 0 ? totalHectares : null,
    hoursSessionCount,
    kmSessionCount,
    hectaresSessionCount,
    lastHourMeter,
    lastHourMeterAt,
    lastOdometer,
    lastOdometerAt,
  };
}

/**
 * Delta do horímetro/odômetro informado pelo operador (assumir → finalizar).
 * Fallback: diferença entre menor e maior leitura no período.
 */
export function computeOperatorMeterDelta(fieldEvents, machineMeterType) {
  const events = [...(fieldEvents || [])].sort(
    (left, right) =>
      new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()
  );

  const useOdometer =
    machineMeterType === "odometer" || machineMeterType === "both";
  const useHours =
    !machineMeterType ||
    machineMeterType === "hour_meter" ||
    machineMeterType === "both";

  let sessionStart = null;
  let totalDelta = 0;
  let sessionCount = 0;

  const readValue = (event) => {
    if (useOdometer && !useHours) {
      return parseOdometer(event.odometer);
    }
    if (useHours && !useOdometer) {
      return parseHourMeter(event.hourMeter);
    }
    const odometer = parseOdometer(event.odometer);
    if (odometer != null) {
      return odometer;
    }
    return parseHourMeter(event.hourMeter);
  };

  events.forEach((event) => {
    const reading = readValue(event);
    if (event.type === "assume_machine" && reading != null) {
      sessionStart = reading;
    } else if (
      event.type === "finish_work" &&
      reading != null &&
      sessionStart != null
    ) {
      const delta = reading - sessionStart;
      if (delta > 0) {
        totalDelta += delta;
        sessionCount += 1;
      }
      sessionStart = null;
    }
  });

  if (sessionCount === 0) {
    const readings = events
      .map((event) => readValue(event))
      .filter((value) => value != null);
    if (readings.length >= 2) {
      const min = Math.min(...readings);
      const max = Math.max(...readings);
      if (max > min) {
        return { delta: max - min, sessionCount: 0, source: "readings" };
      }
    }
    return { delta: null, sessionCount: 0, source: "none" };
  }

  return { delta: totalDelta, sessionCount, source: "sessions" };
}

/** Máquinas de talhão usam ha + horímetro em horas; demais usam km. */
export function resolveMeterMode(operationType, areaHa) {
  if (operationType === "covering" || Number(areaHa) > 0.05) {
    return "hectare";
  }
  return "kilometer";
}

export function emptyMonitoringMetrics() {
  return {
    meterMode: "kilometer",
    fuelPerHa: null,
    fuelPerHour: null,
    fuelPerKm: null,
    gpsMotionValue: null,
    gpsDistanceKm: null,
    gpsInMotionMs: 0,
    operatorMeterDelta: null,
    operatorPanelHours: null,
    operatorPanelKm: null,
    operatorPanelKmFromGps: false,
    operatorPanelHectares: null,
    operatorPanelHoursSessions: 0,
    operatorPanelKmSessions: 0,
    operatorPanelHectaresSessions: 0,
    operatorLastHourMeter: null,
    operatorLastHourMeterAt: null,
    operatorLastOdometer: null,
    operatorLastOdometerAt: null,
    meterDiff: null,
    engineStoppedMs: 0,
    areaHa: 0,
  };
}

export function buildMonitoringMetrics({
  fuelLiters,
  areaHa,
  pathPoints,
  graphSeries,
  fieldEvents,
  operationType,
  machineMeterType = "hour_meter",
}) {
  const motion = calculatePeriodMotionStats(graphSeries || []);
  const engineStoppedMs = calculateEngineOnStoppedTimeMs(graphSeries || []);
  const movingStats = calculateMovingPathDistanceStats(pathPoints || []);
  const normalizedMeterType = machineMeterType || "hour_meter";
  const operator = computeOperatorMeterDelta(fieldEvents, normalizedMeterType);
  const operatorPanel = computeOperatorPanelTotals(
    fieldEvents,
    normalizedMeterType
  );
  const meterMode = resolveMeterMode(operationType, areaHa);

  const inMotionHours = motion.inMotionTimeMs / 3600000;
  const gpsMotionValue =
    meterMode === "hectare" ? inMotionHours : movingStats.distanceKm;

  const liters = Number(fuelLiters) || 0;
  const area = Number(areaHa) || 0;

  const fuelPerHa = area > 0 && liters > 0 ? liters / area : null;
  const fuelPerHour = inMotionHours > 0 && liters > 0 ? liters / inMotionHours : null;
  const fuelPerKm =
    movingStats.distanceKm > 0 && liters > 0
      ? liters / movingStats.distanceKm
      : null;

  const operatorValue = operator.delta;
  let meterDiff = null;
  if (operatorValue != null && gpsMotionValue > 0) {
    meterDiff = operatorValue - gpsMotionValue;
  }

  let operatorPanelKm = operatorPanel.totalKm;
  let operatorPanelKmFromGps = false;
  if (
    normalizedMeterType === "odometer" &&
    operatorPanelKm == null &&
    movingStats.distanceKm > 0
  ) {
    operatorPanelKm = movingStats.distanceKm;
    operatorPanelKmFromGps = true;
  }

  return {
    meterMode,
    fuelPerHa,
    fuelPerHour,
    fuelPerKm,
    gpsMotionValue: gpsMotionValue > 0 ? gpsMotionValue : null,
    gpsDistanceKm: movingStats.distanceKm > 0 ? movingStats.distanceKm : null,
    gpsInMotionMs: motion.inMotionTimeMs,
    operatorMeterDelta: operatorValue,
    operatorPanelHours: operatorPanel.totalHours,
    operatorPanelKm,
    operatorPanelKmFromGps,
    operatorPanelHectares: operatorPanel.totalHectares,
    operatorPanelHoursSessions: operatorPanel.hoursSessionCount,
    operatorPanelKmSessions: operatorPanel.kmSessionCount,
    operatorPanelHectaresSessions: operatorPanel.hectaresSessionCount,
    operatorLastHourMeter: operatorPanel.lastHourMeter,
    operatorLastHourMeterAt: operatorPanel.lastHourMeterAt,
    operatorLastOdometer: operatorPanel.lastOdometer,
    operatorLastOdometerAt: operatorPanel.lastOdometerAt,
    meterDiff,
    engineStoppedMs,
    areaHa: area,
    operatorSessionCount: operator.sessionCount,
  };
}

export function formatFuelRate(value, unit) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)} ${unit}`;
}

export function formatMeterValue(value, meterMode) {
  if (value == null || !Number.isFinite(value)) return "—";
  if (meterMode === "hectare") {
    return `${value.toFixed(2)} h`;
  }
  return `${value.toFixed(2)} km`;
}

export function formatMeterDiff(value, meterMode) {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : "";
  const unit = meterMode === "hectare" ? "h" : "km";
  return `${sign}${value.toFixed(2)} ${unit}`;
}

export function formatOperatorPanelHours(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)} h`;
}

export function formatOperatorPanelHectares(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)} ha`;
}

export function formatOperatorPanelKm(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)} km`;
}

export function formatOperatorOdometerReading(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(1)} km`;
}

export function formatOperatorHourMeterReading(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(1)} h`;
}

export { formatPeriodClockDuration };
